import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { bloodTestsAPI } from '../services/api'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts'
import jsPDF from 'jspdf'
// html2canvas-pro, not html2canvas: the latter cannot parse Tailwind 4's
// oklch() colours and throws during capture. See
// components/recipes/RecipePrintActions.jsx for the full write-up.
import html2canvas from 'html2canvas-pro'
import { withLightTheme } from '../utils/captureLight'
import { useChartTheme } from '../hooks/useChartTheme'
import { contentLanguage } from '../i18n/supported'

// Risk level badge styling
const getRiskBadgeClass = (level) => {
  const levelMap = {
    low: 'bg-success-100 text-success-800 border-success-300',
    moderate: 'bg-warning-100 text-warning-800 border-warning-300',
    high: 'bg-orange-100 text-orange-800 border-orange-300',
    critical: 'bg-danger-100 text-danger-800 border-danger-300',
  }
  return levelMap[level] || 'bg-gray-100 text-gray-800 border-gray-300'
}

// Priority badge styling
const getPriorityBadgeClass = (priority) => {
  const priorityMap = {
    high: 'bg-danger-100 text-danger-800',
    medium: 'bg-warning-100 text-warning-800',
    low: 'bg-gray-100 text-gray-800',
  }
  return priorityMap[priority] || 'bg-gray-100 text-gray-800'
}

// Status badge styling
const getStatusBadgeClass = (status) => {
  const statusMap = {
    optimal: 'badge-success',
    normal: 'badge-success',
    borderline: 'badge-warning',
    slightly_abnormal: 'badge-warning',
    abnormal: 'badge-danger',
    critical: 'badge-danger',
    high: 'badge-warning',
    low: 'badge-warning',
    unknown: 'badge-gray',
  }
  return statusMap[status] || 'badge-gray'
}

export default function NarrativeReport() {
  // VH-133: recharts takes colours as props, so it inherits nothing from
  // the design system — these follow the active theme instead.
  const chart = useChartTheme()
  const { t, i18n } = useTranslation()
  const [selectedTests, setSelectedTests] = useState([])
  const [showPreviousReports, setShowPreviousReports] = useState(false)
  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const reportRef = useRef(null)

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Get user's language
  const currentLanguage = contentLanguage(i18n?.language)

  // Get all user tests for selection
  const { data: allTests, isLoading: testsLoading } = useQuery({
    queryKey: ['bloodTests', 'narrative'],
    queryFn: async () => {
      const response = await bloodTestsAPI.getAll()
      // Filter analyzed tests and sort by date (oldest first, left to right)
      return response.data.tests
        .filter(test => test.status === 'analyzed')
        .sort((a, b) => new Date(a.test_date) - new Date(b.test_date))
    },
    retry: 2,
    staleTime: 30000,
    refetchOnMount: 'always',
  })

  // Get previous reports
  const { data: previousReports } = useQuery({
    queryKey: ['narrativeReports'],
    queryFn: async () => {
      const response = await bloodTestsAPI.listNarrativeReports(5)
      return response.data
    },
  })

  // Generate narrative report mutation
  const reportMutation = useMutation({
    mutationFn: async () => {
      const response = await bloodTestsAPI.generateNarrativeReport(selectedTests, {
        targetLanguage: currentLanguage,
        includeCharts: true,
        includeRiskAssessment: true
      })
      return response.data
    }
  })

  // Load existing report
  const loadReportMutation = useMutation({
    mutationFn: async (reportId) => {
      const response = await bloodTestsAPI.getNarrativeReport(reportId)
      return response.data
    }
  })

  const toggleTestSelection = (testId) => {
    if (selectedTests.includes(testId)) {
      setSelectedTests(selectedTests.filter(id => id !== testId))
    } else {
      setSelectedTests([...selectedTests, testId])
    }
  }

  const handleGenerateReport = () => {
    if (selectedTests.length >= 1) {
      reportMutation.mutate()
    }
  }

  const handleLoadReport = (reportId) => {
    loadReportMutation.mutate(reportId)
    setShowPreviousReports(false)
  }

  // PDF Export function
  const handleExportPdf = async () => {
    if (!reportRef.current) return

    setIsExportingPdf(true)

    try {
      // Create canvas from the report element
      // VH-133: capture in light colours whatever theme is on screen.
      const canvas = await withLightTheme(reportRef.current, () =>
        html2canvas(reportRef.current, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        })
      )

      const _imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('p', 'mm', 'a4')

      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const imgWidth = canvas.width
      const imgHeight = canvas.height
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight)
      const imgX = (pdfWidth - imgWidth * ratio) / 2
      const imgY = 10

      // Calculate how many pages we need
      const scaledHeight = imgHeight * ratio
      const pageHeight = pdfHeight - 20 // Leave margins
      const totalPages = Math.ceil(scaledHeight / pageHeight)

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage()
        }

        // Calculate the portion of the image for this page
        const sourceY = page * (pageHeight / ratio)
        const sourceHeight = Math.min(pageHeight / ratio, imgHeight - sourceY)

        // Create a temporary canvas for this page portion
        const pageCanvas = document.createElement('canvas')
        pageCanvas.width = imgWidth
        pageCanvas.height = sourceHeight
        const ctx = pageCanvas.getContext('2d')

        ctx.drawImage(
          canvas,
          0, sourceY, imgWidth, sourceHeight,
          0, 0, imgWidth, sourceHeight
        )

        const pageImgData = pageCanvas.toDataURL('image/png')
        const destHeight = sourceHeight * ratio

        pdf.addImage(pageImgData, 'PNG', imgX, imgY, imgWidth * ratio, destHeight)
      }

      // Add footer to last page
      pdf.setFontSize(8)
      pdf.setTextColor(128, 128, 128)
      pdf.text(
        `Generated by VitaHub - ${new Date().toLocaleDateString()}`,
        pdfWidth / 2,
        pdfHeight - 5,
        { align: 'center' }
      )

      // Save the PDF
      const fileName = `health-report-${new Date().toISOString().split('T')[0]}.pdf`
      pdf.save(fileName)

    } catch (error) {
      console.error('Error exporting PDF:', error)
    } finally {
      setIsExportingPdf(false)
    }
  }

  const report = reportMutation.data || loadReportMutation.data

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link to="/blood-tests" className="text-primary-600 hover:text-primary-700 text-sm mb-2 inline-block">
          {t('common.back')} {t('nav.bloodTests')}
        </Link>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('reports.narrative.title', 'Health Report')}</h1>
            <p className="text-gray-600 mt-1">{t('reports.narrative.subtitle', 'AI-powered comprehensive health analysis and recommendations')}</p>
          </div>
          {report && (
            <button
              onClick={handleExportPdf}
              disabled={isExportingPdf}
              className="btn-primary flex items-center gap-2"
            >
              {isExportingPdf ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('reports.narrative.exporting', 'Exporting...')}
                </>
              ) : (
                <>
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {t('reports.narrative.exportPdf', 'Export PDF')}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Test Selection or Previous Reports */}
      {!report && (
        <div className="card mb-8">
          <div className="card-header flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">
                {showPreviousReports
                  ? t('reports.narrative.previousReports', 'Previous Reports')
                  : t('reports.narrative.selectTests', 'Select Blood Tests')}
              </h2>
              {!showPreviousReports && (
                <p className="text-sm text-gray-600 mt-1">
                  {t('reports.narrative.selectHint', 'Select one or more analyzed tests for your report')}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              {previousReports && previousReports.length > 0 && (
                <button
                  onClick={() => setShowPreviousReports(!showPreviousReports)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {showPreviousReports
                    ? t('reports.narrative.newReport', 'Create New Report')
                    : t('reports.narrative.viewPrevious', 'View Previous Reports')}
                </button>
              )}
              {!showPreviousReports && (
                <button
                  onClick={handleGenerateReport}
                  disabled={selectedTests.length < 1 || reportMutation.isPending}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {reportMutation.isPending ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('reports.narrative.generating', 'Generating...')}
                    </>
                  ) : (
                    t('reports.narrative.generate', 'Generate Report')
                  )}
                </button>
              )}
            </div>
          </div>
          <div className="card-body">
            {showPreviousReports ? (
              <div className="space-y-3">
                {previousReports?.map((report) => (
                  <button
                    key={report.report_id}
                    onClick={() => handleLoadReport(report.report_id)}
                    className="w-full p-4 border rounded-lg text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-gray-900">
                          {t('reports.narrative.reportFrom', 'Report from')} {new Date(report.generated_at).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-600">
                          {report.blood_test_ids.length} {t('reports.narrative.testsAnalyzed', 'tests analyzed')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-1 bg-gray-100 rounded">{report.language_code.toUpperCase()}</span>
                        {report.has_pdf && (
                          <span className="text-xs px-2 py-1 bg-primary-100 text-primary-800 rounded">PDF</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : testsLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : !allTests || allTests.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('reports.narrative.noAnalyzedTests', 'No analyzed blood tests available.')}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allTests.map((test) => (
                  <button
                    key={test.id}
                    onClick={() => toggleTestSelection(test.id)}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      selectedTests.includes(test.id)
                        ? 'border-primary-600 bg-primary-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {new Date(test.test_date).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-600">{test.lab_name || 'Blood Test'}</p>
                        {test.health_score && (
                          <p className="text-sm font-medium text-primary-600 mt-1">
                            Score: {Math.round(test.health_score)}/100
                          </p>
                        )}
                      </div>
                      {selectedTests.includes(test.id) && (
                        <svg className="h-6 w-6 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error State */}
      {(reportMutation.isError || loadReportMutation.isError) && (
        <div className="bg-danger-50 border-l-4 border-danger-500 p-4 rounded-r-md mb-8">
          <p className="text-sm text-danger-700">
            {t('reports.narrative.error', 'Failed to generate report. Please try again.')}
          </p>
        </div>
      )}

      {/* Report Content */}
      {report && (
        <div ref={reportRef} className="bg-surface-container">
          {/* Report Header */}
          <div className="text-center mb-8 pb-6 border-b">
            <h2 className="text-2xl font-bold text-gray-900">{t('reports.narrative.healthReport', 'Health Analysis Report')}</h2>
            <p className="text-gray-600 mt-1">
              {t('reports.narrative.generatedOn', 'Generated on')} {new Date(report.generated_at).toLocaleDateString()}
            </p>
            <p className="text-sm text-gray-500">
              {report.blood_test_ids.length} {t('reports.narrative.testsIncluded', 'blood tests included')}
            </p>
          </div>

          {/* Executive Summary */}
          <div className="card mb-8">
            <div className="card-header bg-primary-50">
              <h3 className="text-xl font-semibold text-primary-900">
                {t('reports.narrative.executiveSummary', 'Executive Summary')}
              </h3>
            </div>
            <div className="card-body">
              <div className="prose max-w-none text-gray-700 whitespace-pre-line">
                {report.executive_summary}
              </div>
            </div>
          </div>

          {/* Risk Assessment */}
          {report.risk_assessment && (
            <div className="card mb-8">
              <div className="card-header">
                <h3 className="text-xl font-semibold">{t('reports.narrative.riskAssessment', 'Risk Assessment')}</h3>
              </div>
              <div className="card-body">
                <div className="flex items-center gap-4 mb-6">
                  <div className={`px-4 py-2 rounded-lg border-2 font-semibold ${getRiskBadgeClass(report.risk_assessment.overall_risk_level)}`}>
                    {t(`reports.narrative.risk.${report.risk_assessment.overall_risk_level}`, report.risk_assessment.overall_risk_level.toUpperCase())}
                  </div>
                  {report.risk_assessment.follow_up_timeframe && (
                    <p className="text-gray-600">{report.risk_assessment.follow_up_timeframe}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Areas of Concern */}
                  {report.risk_assessment.areas_of_concern?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-danger-700 mb-2">
                        {t('reports.narrative.areasOfConcern', 'Areas of Concern')}
                      </h4>
                      <ul className="space-y-1">
                        {report.risk_assessment.areas_of_concern.map((concern, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <svg className="h-5 w-5 text-danger-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            {concern}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Improving Factors */}
                  {report.risk_assessment.improving_factors?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-success-700 mb-2">
                        {t('reports.narrative.improvingFactors', 'Improving Factors')}
                      </h4>
                      <ul className="space-y-1">
                        {report.risk_assessment.improving_factors.map((factor, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <svg className="h-5 w-5 text-success-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            {factor}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Health Score Chart */}
          {report.charts_data?.health_score_trend?.length > 1 && (
            <div className="card mb-8">
              <div className="card-header">
                <h3 className="text-xl font-semibold">{t('reports.narrative.healthScoreTrend', 'Health Score Trend')}</h3>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={report.charts_data.health_score_trend} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                    <XAxis dataKey="date" tickFormatter={(date) => new Date(date).toLocaleDateString()} />
                    <YAxis domain={[0, 100]} />
                    <Tooltip
                      labelFormatter={(date) => new Date(date).toLocaleDateString()}
                      formatter={(value) => [Math.round(value), 'Health Score']}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="score"
                      name={t('reports.narrative.healthScore', 'Health Score')}
                      stroke={chart.info}
                      strokeWidth={2}
                      dot={{ r: 6 }}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Detailed Marker Analysis */}
          {report.detailed_analysis?.length > 0 && (
            <div className="card mb-8">
              <div className="card-header">
                <h3 className="text-xl font-semibold">{t('reports.narrative.detailedAnalysis', 'Detailed Marker Analysis')}</h3>
              </div>
              <div className="card-body p-0">
                <div className="divide-y divide-gray-200">
                  {report.detailed_analysis.map((marker, idx) => (
                    <div key={idx} className="p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">{marker.marker_name}</h4>
                          {marker.category && (
                            <p className="text-xs text-gray-500">{marker.category}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">
                            {marker.current_value} {marker.unit}
                          </p>
                          {marker.current_status && (
                            <span className={`badge ${getStatusBadgeClass(marker.current_status)} text-xs`}>
                              {marker.current_status}
                            </span>
                          )}
                        </div>
                      </div>
                      {marker.clinical_context && (
                        <p className="text-sm text-gray-700 mb-2">{marker.clinical_context}</p>
                      )}
                      {marker.trend_description && (
                        <p className="text-sm text-gray-500 italic">{marker.trend_description}</p>
                      )}
                      {marker.recommendation && (
                        <p className="text-sm text-primary-700 mt-2 bg-primary-50 p-2 rounded">
                          {marker.recommendation}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Marker Charts */}
          {report.charts_data?.marker_charts?.filter(m => m.is_concern)?.length > 0 && (
            <div className="card mb-8">
              <div className="card-header">
                <h3 className="text-xl font-semibold">{t('reports.narrative.markerTrends', 'Key Marker Trends')}</h3>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {report.charts_data.marker_charts.filter(m => m.is_concern).slice(0, 4).map((marker, idx) => (
                    <div key={idx} className="border rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">{marker.marker_name}</h4>
                      <p className="text-xs text-gray-500 mb-2">
                        {t('reports.narrative.reference', 'Reference')}: {marker.reference_range} {marker.unit}
                      </p>
                      <ResponsiveContainer width="100%" height={150}>
                        <LineChart data={marker.data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                          <XAxis dataKey="date" tickFormatter={(date) => new Date(date).toLocaleDateString()} tick={{ fontSize: 10, fill: chart.axis }} />
                          <YAxis tick={{ fontSize: 10, fill: chart.axis }} />
                          <Tooltip
                            labelFormatter={(date) => new Date(date).toLocaleDateString()}
                            formatter={(value) => [value?.toFixed(1), marker.marker_name]}
                          />
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke={chart.danger}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Recommendations */}
          {report.recommendations?.length > 0 && (
            <div className="card mb-8">
              <div className="card-header bg-success-50">
                <h3 className="text-xl font-semibold text-success-900">
                  {t('reports.narrative.recommendations', 'Recommendations')}
                </h3>
              </div>
              <div className="card-body">
                <div className="space-y-4">
                  {report.recommendations.map((rec, idx) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg border-l-4 ${
                        rec.priority === 'high' ? 'bg-danger-50 border-danger-500' :
                        rec.priority === 'medium' ? 'bg-warning-50 border-warning-500' :
                        'bg-gray-50 border-gray-400'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${getPriorityBadgeClass(rec.priority)}`}>
                          {rec.priority?.toUpperCase()}
                        </span>
                        {rec.category && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded bg-primary-100 text-primary-800">
                            {rec.category.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-900 font-medium">{rec.recommendation}</p>
                      {rec.rationale && (
                        <p className="text-sm text-gray-600 mt-2">{rec.rationale}</p>
                      )}
                      {rec.related_markers?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {rec.related_markers.map((marker) => (
                            <span key={marker} className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded">
                              {marker}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className="text-center text-sm text-gray-500 mt-8 p-4 bg-gray-50 rounded-lg">
            <p className="font-medium mb-1">{t('reports.narrative.disclaimer', 'Disclaimer')}</p>
            <p>
              {t('reports.narrative.disclaimerText', 'This report is for informational purposes only and should not replace professional medical advice. Always consult with a healthcare provider for diagnosis and treatment.')}
            </p>
          </div>

          {/* Generate New Report Button */}
          <div className="text-center mt-8">
            <button
              onClick={() => {
                reportMutation.reset()
                loadReportMutation.reset()
                setSelectedTests([])
              }}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('reports.narrative.generateNew', 'Generate New Report')}
            </button>
          </div>
        </div>
      )}

      {/* No Report State */}
      {!report && selectedTests.length === 0 && !showPreviousReports && !reportMutation.isPending && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-md">
          <p className="text-sm text-blue-700">
            {t('reports.narrative.selectPrompt', 'Select at least one blood test and click "Generate Report" to create your comprehensive health analysis.')}
          </p>
        </div>
      )}
    </div>
  )
}
